import React, { useEffect, useRef } from "react";
import { animate } from "animejs";
import { createTimeline } from "animejs/timeline";
import { useDrawLoop } from "./utils";
import "./styles.css";

export const handler = ({ inputs, mechanic }) => {
  const {
    width,
    height,
    backgroundColor,
    primaryColor,
    secondaryColor,
    duration,
    enableAnimation
  } = inputs;

  const startTime = useRef(Date.now());
  const isPlaying = useRef(true);
  const frameCount = useDrawLoop(isPlaying.current);
  const timelineRef = useRef(null);
  const box1Ref = useRef(null);
  const box2Ref = useRef(null);
  const box3Ref = useRef(null);

  // Initialize anime.js v4 timeline once
  useEffect(() => {
    if (enableAnimation && !timelineRef.current && box1Ref.current) {
      const tl = createTimeline({
        defaults: {
          duration: duration * 1000,
          ease: 'inOutQuad'
        },
        autoplay: false
      });

      tl.add(box1Ref.current, {
        rotate: { to: 360 },
        scale: { to: [1, 1.5, 1] },
        translateY: { to: [-50, 50, 0] }
      }, 0);

      tl.add(box2Ref.current, {
        rotate: { to: -360 },
        scale: { to: [1, 0.5, 1] },
        translateX: { to: [-50, 50, 0] }
      }, 0);

      tl.add(box3Ref.current, {
        rotate: { to: 720 },
        scale: { to: [1, 2, 1] },
        borderRadius: { to: ['8px', '50%', '8px'] }
      }, 0);

      timelineRef.current = tl;
    }
  }, [box1Ref.current, box2Ref.current, box3Ref.current, duration, enableAnimation]);

  // Handle animation or static rendering
  useEffect(() => {
    if (!enableAnimation) {
      // Static mode: immediately call done
      mechanic.done();
      return;
    }

    // Animated mode: update timeline and call frame
    if (timelineRef.current) {
      const elapsed = Date.now() - startTime.current;
      timelineRef.current.seek(elapsed);

      if (elapsed < duration * 1000) {
        mechanic.frame();
      } else if (isPlaying.current) {
        isPlaying.current = false;
        mechanic.done();
      }
    }
  }, [frameCount, duration, enableAnimation]);

  // Calculate static or animated position
  const getTransform = (boxNum) => {
    if (!enableAnimation) {
      // Static position
      return 'none';
    }
    // Animation is handled by anime.js
    return undefined;
  };

  return (
    <div
      className="anime-container"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: backgroundColor,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "40px",
        position: "relative"
      }}
    >
      <div
        ref={box1Ref}
        className="anime-box"
        style={{
          width: "80px",
          height: "80px",
          backgroundColor: primaryColor,
          borderRadius: "8px",
          transform: getTransform(1)
        }}
      />
      
      <div
        ref={box2Ref}
        className="anime-box"
        style={{
          width: "80px",
          height: "80px",
          backgroundColor: secondaryColor,
          borderRadius: "8px",
          transform: getTransform(2)
        }}
      />
      
      <div
        ref={box3Ref}
        className="anime-box"
        style={{
          width: "80px",
          height: "80px",
          backgroundColor: primaryColor,
          borderRadius: "8px",
          transform: getTransform(3)
        }}
      />
    </div>
  );
};

export const inputs = {
  width: {
    type: "number",
    default: 600
  },
  height: {
    type: "number",
    default: 400
  },
  backgroundColor: {
    type: "color",
    model: "hex",
    default: "#1a1a1a"
  },
  primaryColor: {
    type: "color",
    model: "hex",
    default: "#6691cb"
  },
  secondaryColor: {
    type: "color",
    model: "hex",
    default: "#fcf3c2"
  },
  enableAnimation: {
    type: "boolean",
    default: true,
    label: "Enable Animation"
  },
  duration: {
    type: "number",
    default: 3,
    min: 1,
    max: 10,
    slider: true,
    label: "Duration (seconds)",
    editable: (inputs) => inputs.enableAnimation
  }
};

export const settings = {
  engine: require("@mechanic-design/engine-react"),
  name: "Anime.js Optional Animation",
  animated: true, // Must stay true to allow frame recording
  hideFunction: true
};
